/**
 * Q&A System Dashboard
 *
 * Main dashboard for managing the Q&A system including:
 * - Viewing and managing question wheels
 * - Generating answers
 * - Approving/rejecting pending answers
 * - Entity analytics
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { Loader2, CheckCircle, XCircle, Clock, Sparkles, BarChart3 } from 'lucide-react';
import { QuestionWheelsPanel } from '@/components/qa/QuestionWheelsPanel';
import { PendingAnswersPanel } from '@/components/qa/PendingAnswersPanel';
import { EntityAnalyticsPanel } from '@/components/qa/EntityAnalyticsPanel';
import { AnswerGenerationPanel } from '@/components/qa/AnswerGenerationPanel';
import { SectorTopicsAvailabilityPanel } from '@/components/qa/SectorTopicsAvailabilityPanel';
import { SectorTopicsPanel } from '@/components/qa/SectorTopicsPanel';

export default function QASystemDashboard() {
  const { selectedSite } = useSite();
  const [stats, setStats] = useState({
    totalWheels: 0,
    totalQuestions: 0,
    pendingAnswers: 0,
    approvedAnswers: 0,
    totalEntities: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (selectedSite) {
      loadStats();
    }
  }, [selectedSite]);

  const loadStats = async () => {
    if (!selectedSite) return;

    setLoading(true);
    try {
      // Check if site has a sector assigned
      const siteId = selectedSite.id || selectedSite._id;
      const sectorId = selectedSite.sector_id;

      // Load stats from multiple endpoints in parallel
      const promises = [
        authFetch(`/qa/sites/${siteId}/answers/pending?status=pending&limit=1`),
        authFetch(`/qa/sites/${siteId}/answers/pending?status=approved&limit=1`)
      ];

      // Only fetch question wheels if sector is assigned
      if (sectorId) {
        promises.unshift(authFetch(`/sectors/${sectorId}/question-wheels`));
      }

      const responses = await Promise.all(promises);
      const [wheelsRes, pendingRes, approvedRes] = sectorId
        ? responses
        : [{ json: async () => ({ success: true, wheels: [] }) }, ...responses];

      const wheelsData = await wheelsRes.json();
      const pendingData = await pendingRes.json();
      const approvedData = await approvedRes.json();

      setStats({
        totalWheels: wheelsData.wheels?.length || 0,
        totalQuestions: wheelsData.wheels?.reduce((acc: number, w: any) =>
          acc + (w.question_count || 0), 0) || 0,
        pendingAnswers: pendingData.total || 0,
        approvedAnswers: approvedData.total || 0,
        totalEntities: 0 // Will be populated from entity analytics
      });
    } catch (error) {
      console.error('Failed to load Q&A stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedSite) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Please select a site to manage Q&A content
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Q&A System</h1>
        <p className="text-muted-foreground">
          AI-powered question and answer management for {selectedSite.domain}
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Question Wheels</CardTitle>
            <Sparkles className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <div className="text-2xl font-bold">{stats.totalWheels}</div>
            )}
            <p className="text-xs text-muted-foreground">
              Available for your sector
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Questions</CardTitle>
            <Sparkles className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <div className="text-2xl font-bold">{stats.totalQuestions}</div>
            )}
            <p className="text-xs text-muted-foreground">
              Across all wheels
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Answers</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <div className="text-2xl font-bold text-yellow-600">
                {stats.pendingAnswers}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Awaiting review
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved Answers</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <div className="text-2xl font-bold text-green-600">
                {stats.approvedAnswers}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Published answers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Entity Links</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <div className="text-2xl font-bold">{stats.totalEntities}</div>
            )}
            <p className="text-xs text-muted-foreground">
              Tagged entities
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="topics">Topics</TabsTrigger>
          <TabsTrigger value="wheels">Question Wheels</TabsTrigger>
          <TabsTrigger value="generate">Generate Answers</TabsTrigger>
          <TabsTrigger value="pending">
            Pending Answers
            {stats.pendingAnswers > 0 && (
              <Badge variant="secondary" className="ml-2">
                {stats.pendingAnswers}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="analytics">Entity Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Step 1: Topic Generation */}
          <SectorTopicsAvailabilityPanel
            siteId={selectedSite.id || selectedSite._id}
            sectorId={selectedSite.sector_id}
          />

          {/* Getting Started Guide */}
          <Card>
            <CardHeader>
              <CardTitle>Q&A System Workflow</CardTitle>
              <CardDescription>
                Follow these steps to create AI-powered Q&A content for your site
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-semibold">Generate Topics (Above)</h3>
                  <p className="text-sm text-muted-foreground">
                    Analyze your product feed to identify relevant topics for your sector
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-semibold">Browse Question Wheels</h3>
                  <p className="text-sm text-muted-foreground">
                    Explore pre-built question sets based on identified topics
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-semibold">Generate AI Answers</h3>
                  <p className="text-sm text-muted-foreground">
                    Use AI to generate contextual answers based on your products and site content
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                  4
                </div>
                <div>
                  <h3 className="font-semibold">Review & Approve</h3>
                  <p className="text-sm text-muted-foreground">
                    Review generated answers and approve them for publication
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                  5
                </div>
                <div>
                  <h3 className="font-semibold">Monitor Performance</h3>
                  <p className="text-sm text-muted-foreground">
                    Track entity clicks and answer engagement through analytics
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="topics">
          <SectorTopicsPanel
            siteId={selectedSite.id || selectedSite._id}
            sectorId={selectedSite.sector_id}
          />
        </TabsContent>

        <TabsContent value="wheels">
          <QuestionWheelsPanel siteId={selectedSite.id} sectorId={selectedSite.sector_id} />
        </TabsContent>

        <TabsContent value="generate">
          <AnswerGenerationPanel siteId={selectedSite.id} sectorId={selectedSite.sector_id} />
        </TabsContent>

        <TabsContent value="pending">
          <PendingAnswersPanel siteId={selectedSite.id} onUpdate={loadStats} />
        </TabsContent>

        <TabsContent value="analytics">
          <EntityAnalyticsPanel siteId={selectedSite.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
